import { widgetVisualTreeKey } from "./keys.js";

/**
 * Active preview phase replaces `items` of the container named by `phases[].container`.
 * Visual indices walk `view` until that container, then `phases[n].items`.
 */

export interface PhaseMatch {
  entry: Record<string, unknown>;
  index: number;
}

export interface VisualWalk {
  node: Record<string, unknown> | null;
  items: unknown[] | null;
  index: number | null;
  inPhase: boolean;
}

export function findPhase(
  spec: Record<string, unknown> | null | undefined,
  phaseId: string | null | undefined,
): PhaseMatch | null {
  if (!spec || !phaseId || phaseId === "data") return null;
  const phases = spec.phases;
  if (!Array.isArray(phases)) return null;
  const index = phases.findIndex((entry) => {
    return Boolean(entry && typeof entry === "object" && (entry as Record<string, unknown>).id === phaseId);
  });
  if (index < 0) return null;
  return { entry: phases[index] as Record<string, unknown>, index };
}

function asNode(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? value as Record<string, unknown> : null;
}

function phaseItems(entry: Record<string, unknown>): unknown[] | null {
  return Array.isArray(entry.items) ? entry.items : null;
}

/** Walk `view` indices, stepping into the active phase at its container. */
export function walkVisual(
  spec: Record<string, unknown> | null | undefined,
  indices: number[],
  phaseId: string | null | undefined,
): VisualWalk {
  const match = findPhase(spec, phaseId);
  let node = asNode(spec?.view);
  let items: unknown[] | null = null;
  let index: number | null = null;
  let inPhase = false;
  for (const childIndex of indices) {
    if (!node) return { node: null, items, index: childIndex, inPhase };
    const usePhase = !inPhase && match != null && node.id === match.entry.container;
    const list = usePhase ? phaseItems(match.entry) : node.items;
    items = Array.isArray(list) ? list : null;
    index = childIndex;
    if (usePhase) inPhase = true;
    node = asNode(items?.[childIndex]);
  }
  return { node, items, index, inPhase };
}

/** Direct children painted for this node: phase items when it is the phase container. */
export function visualChildren(
  node: Record<string, unknown> | null | undefined,
  spec: Record<string, unknown> | null | undefined,
  phaseId: string | null | undefined,
): unknown[] {
  const match = findPhase(spec, phaseId);
  if (match && node?.id === match.entry.container) {
    return phaseItems(match.entry) ?? [];
  }
  return Array.isArray(node?.items) ? node.items : [];
}

/** First visual tree key whose node satisfies `matches` (preview phase tree). */
export function findWidgetVisualKey(
  spec: Record<string, unknown> | null | undefined,
  widgetName: string,
  phaseId: string | null | undefined,
  matches: (node: Record<string, unknown>) => boolean,
): string | null {
  const root = asNode(spec?.view);
  if (!root) return null;

  const visit = (node: Record<string, unknown>, indices: number[]): string | null => {
    if (matches(node)) return widgetVisualTreeKey(widgetName, indices);
    for (const [index, child] of visualChildren(node, spec, phaseId).entries()) {
      const childNode = asNode(child);
      if (!childNode) continue;
      const hit = visit(childNode, [...indices, index]);
      if (hit) return hit;
    }
    return null;
  };

  return visit(root, []);
}
